import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const TransferElection = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Transfer Election Statement (TE) | IRS Tax Credit Filing"
          description="Learn what a Transfer Election Statement is, who must file it, what information is required, and how it’s attached to Form 3800 and Form 990-T."
          keywords=""
          canonical="resources/tax-exempt/transfer-election-statement/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight">Transfer Election </span>
          </span>{' '}
          Statement{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          For exempt organizations filing{' '}
          <span style={{ fontWeight: 600 }}> Form 990-T</span>, the Transfer
          Election Statement is generally included as an attachment to the
          return. Refer to our{' '}
          <Link
            href="/tax-exempt/e-file-irs-form-990-t/"
            sx={{ color: '#0202ff' }}
          >
            {' '}
            Form 990-T
          </Link>{' '}
          reference for additional details.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=transfer`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/transfer-election-statement/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Transfer Election Statement at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is a Transfer Election Statement?
                        </strong>{' '}
                        A{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Transfer Election Statement
                        </strong>{' '}
                        is a written statement used to document the transfer of
                        an eligible tax credit from an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          eligible taxpayer (transferor)
                        </strong>{' '}
                        to a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          transferee taxpayer
                        </strong>{' '}
                        under{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Section 6418
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses It?
                        </strong>{' '}
                        Both the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          transferor and transferee
                        </strong>{' '}
                        generally attach the transfer election statement to
                        their respective federal tax returns for the year of the
                        credit transfer.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Does It Report?
                        </strong>{' '}
                        The statement identifies the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          transferor and transferee, the transferred credit, IRS
                          registration number, credit amount, and payment
                          information
                        </strong>{' '}
                        required for the transfer.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Which Form Is Used?
                        </strong>{' '}
                        <Link
                          href="/resources/tax-exempt/form-3800-schedule-a/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            Schedule A (Form 3800)
                          </strong>
                        </Link>
                        , Transfer Election Statement, can be used to provide
                        the required transfer-election information. It is filed
                        with{' '}
                        <Link
                          href="/resources/tax-exempt/form-3800-business-credits/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>Form 3800</strong>
                        </Link>{' '}
                        when reporting eligible transferred credit.{' '}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is a Transfer Election Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whyis" sx={{ color: '#0202ff' }}>
                          Why is a Transfer Election Statement Used?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who uses a Transfer Election Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What credits commonly use a TE Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#info" sx={{ color: '#0202ff' }}>
                          What Information is Included in a TE Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#forms" sx={{ color: '#0202ff' }}>
                          What forms are filed along with a TE Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#you" sx={{ color: '#0202ff' }}>
                          Why you should use a Transfer Election Statement
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How the Transfer Election Process Works (in steps)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#irs" sx={{ color: '#0202ff' }}>
                          IRS Format for TE Statement vs Custom Format
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What happens if you don&apos;t have a TE Statement?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#source" sx={{ color: '#0202ff' }}>
                          Source & IRS References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is a Transfer Election Statement?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  A Transfer Election Statement is the official document that
                  confirms a taxpayer has elected to transfer a tax credit
                  rather than claim it themselves. It serves as proof to the IRS
                  that the credit has changed ownership and that the transferee
                  is entitled to claim it on their tax return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whyis"
                >
                  Why is a Transfer Election Statement used?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Not all taxpayers who earn tax credits can benefit from them
                  directly. Instead of letting credits go unused, U.S. tax law
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    allows certain credits to be transferred or sold.
                  </span>
                </Typography>
                <Typography variant="body1">
                  The TE Statement exists to:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Document the transfer clearly</li>
                  <li>Prove IRS compliance</li>
                  <li>Prevent misuse or duplicate claims</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Without a TE Statement, the IRS may{' '}
                  <span style={{ fontWeight: 600 }}>
                    disallow the transferred credit.
                  </span>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who uses a Transfer Election Statement?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A Transfer Election (TE) Statement always includes{' '}
                  <span style={{ fontWeight: 600 }}>two parties,</span> and each
                  one must attach it to their tax filing.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  1. Transferor (Eligible Taxpayer)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Earned the original tax credit </li>
                  <li>Elects to transfer all or part of the credit </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  2. Transferee (Credit Buyer)
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>Receives or purchases the credit </li>
                  <li>Claims the credit on their tax return </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What credits commonly use a TE Statement?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Transfer Election Statements are typically used for{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    transferable credits,
                  </span>{' '}
                  including:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Alternative Fuel Vehicle Refueling Property Credit (Section
                    30C){' '}
                  </li>
                  <li>Renewable Electricity Production Credit (Section 45) </li>
                  <li>Carbon Oxide Sequestration Credit (Section 45Q) </li>
                  <li>
                    Zero-Emission Nuclear Power Production Credit (Section
                    45U){' '}
                  </li>
                  <li>Clean Hydrogen Production Credit (Section 45V) </li>
                  <li>
                    Advanced Manufacturing Production Credit (Section 45X){' '}
                  </li>
                  <li>Energy Investment Credit (Section 48) </li>
                  <li>
                    Qualifying Advanced Energy Project Credit (Section 48C){' '}
                  </li>
                  <li>Clean Electricity Production Credit (Section 45Y) </li>
                  <li>Clean Fuel Production Credit (Section 45Z) </li>
                  <li>Clean Electricity Investment Credit (Section 48E) </li>
                  <li>Small Agri-Biodiesel Producer Credit (Section 40A) </li>
                  <li>Clean & Renewable Energy Credits (IRA-expanded) </li>
                  <li>Research & Development (R&D) Credits </li>
                  <li>Clean Vehicle-Related Transferable Credits </li>
                  <li>Other IRA-Eligible General Business Credits </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Many of these credits were expanded or made transferable under
                  the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Inflation Reduction Act (IRA).
                  </span>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="info"
                >
                  What information is included in a TE Statement?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A real Transfer Election Statement should clearly include:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Names, addresses, and TINs for both sides </li>
                  <li>The kind of tax credit being moved </li>
                  <li>How much credit was moved </li>
                  <li>The year of the tax credit </li>
                  <li>If necessary, your IRS registration number </li>
                  <li>When and how much to pay for the credit </li>
                  <li>Required legal representations </li>
                  <li>Both parties must sign under threat of perjury. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  All required details must be complete and accurate for the
                  statement to be accepted.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="forms"
                >
                  What forms are filed along with a TE Statement?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Transfer Election Statement
                  </span>{' '}
                  doesn’t get filed on its own. Think of it as a supporting
                  document that travels with the tax return where the
                  transferred credit is claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  It is usually attached to:{' '}
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li style={{ fontWeight: 600 }}>
                    Form 3800 - Credit for General Business{' '}
                  </li>
                  <li>
                    Other business or exempt organization returns that
                    apply{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    transferor (the person who originally held the credit)
                  </span>{' '}
                  and the{' '}
                  <span style={{ fontWeight: 600 }}>
                    transferee (the person who is claiming the credit)
                  </span>
                  must both include it on their returns for the year that the
                  credit is reported.
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips:</b> If you want a clear, step-by-step guide on how to
                  report credits on Form 3800 and Schedule A, check out our
                  guide here:{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 3800 Schedule A.
                  </span>{' '}
                  It has examples and tips to help you avoid making mistakes.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="you"
                >
                  Why you should use a Transfer Election Statement
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Taxpayers can do the following with a TE Statement:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Make money off credits they can&apos;t use </li>
                  <li>Make sure you follow the IRS rules </li>
                  <li>Don&apos;t lose credits that are worth a lot. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  It gives buyers a legal way to lower their taxes by using
                  approved credits.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How the Transfer Election Process works (in steps)
                </Typography>

                <Box component="ol" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>A taxpayer gets a tax credit that they can use </li>
                  <li>The taxpayer chooses to move the credit </li>
                  <li>The credit goes to another taxpayer. </li>
                  <li>A Transfer Election Statement is made </li>
                  <li>The person who gets the credit fills out Form 3800. </li>
                  <li>
                    Both sides send in their TE Statement with their
                    returns.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="irs"
                >
                  IRS Format for TE Statement vs Custom Format
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To help taxpayers, the IRS has recently released a standard
                  format for Transfer Election Statements. You don&apos;t have
                  to use this format, though. Taxpayers now have two good
                  choices.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Option 1: IRS-Provided Format for a Transfer Election
                  Statement
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  The IRS-recommended format (Schedule A of Form 3800)
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Fully follows IRS requirements </li>
                  <li>Covers all required Transfer Election details </li>
                  <li>
                    Uses{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule A (Form 3800)
                    </span>{' '}
                    to report the credit transfer{' '}
                  </li>
                  <li>Makes the process easier for first-time filers </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Option 2: Custom Format for Transfer Election Statement
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Taxpayers can also use their own format as long as it:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    It is called &quot;Transfer Election Statement.&quot;{' '}
                  </li>
                  <li>Has all the IRS information it needs </li>
                  <li>Both parties have signed it.</li>
                  <li>Is attached to the right tax returns </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A lot of tax professionals and businesses use this choice.
                </Typography>
                <Alert
                  severity="info"
                  // icon={< />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Important:</b> The IRS cares more about the information
                  than the way it is presented. If they are complete, both the
                  IRS format and custom formats are fine.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="happen"
                >
                  What happens if you don&apos;t have a TE Statement?
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you don&apos;t attach a required TE Statement:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>You may not get the credit you transferred. </li>
                  <li>The return might take longer or be looked over. </li>
                  <li>You may need to send more paperwork to the IRS. </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id="source"
                      sx={{
                        px: 0,
                        m: 0,
                        scrollMarginTop: 90,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The IRS has given us this information about transferable
                        tax credits and Transfer Election Statements. To find
                        out if you qualify, what documents you need, and how to
                        report your credit transfers, look at the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i3800.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions for Form 3800 Schedule A
                        </Link>{' '}
                        and any other IRS guidance that applies.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Properly Document Your Tax Credit Transfer
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              A Transfer Election Statement supports accurate reporting and IRS
              compliance.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=transfer`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default TransferElection;
